import {
  RefetchOptions,
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query';
import {
  Appointment,
  DocumentReference,
  Encounter,
  FhirResource,
  HealthcareService,
  Location,
  Patient,
  Practitioner,
  QuestionnaireResponse,
} from 'fhir/r4b';
import { useCallback, useEffect, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  extractPatientConditionPhotoRefsFromAppointmentData,
  PatientConditionPhotoRef,
} from 'src/features/visits/telemed/utils/appointments';
import { useApiClients } from 'src/hooks/useAppClients';
import { isLocationInPerson, isLocationVirtual } from 'utils/lib/fhir/location';
import { useSuccessQuery } from 'utils/lib/frontend/hooks/useSuccessQuery';
import { AllChartValues, NOTE_TYPE, RequestedFields } from 'utils/lib/types/api/chart-data/chart-data.types';
import { DeleteChartDataRequest } from 'utils/lib/types/api/chart-data/delete-chart-data.types';
import { GetChartDataResponse } from 'utils/lib/types/api/chart-data/get-chart-data.types';
import { SaveChartDataRequest } from 'utils/lib/types/api/chart-data/save-chart-data.types';
import { PromiseReturnType } from 'utils/lib/types/common';
import {
  SCHOOL_WORK_NOTE_CODE,
  SCHOOL_WORK_NOTE_TEMPLATE_CODE,
} from 'utils/lib/types/data/paperwork/paperwork.constants';
import { APIErrorCode } from 'utils/lib/types/errors';
import { create } from 'zustand';
import { OystehrTelemedAPIClient } from '../../api/oystehrApi';
import { invalidateChart } from '../../hooks/chartSectionCache';
import { useGetAppointmentAccessibility } from '../../hooks/useGetAppointmentAccessibility';
import { useOystehrAPIClient } from '../../hooks/useOystehrAPIClient';
import { getAppointmentValues, getEncounterValues } from './parser/extractors';
import { parseBundle } from './parser/parser';
import { VisitMappedData, VisitResources } from './parser/types';
import { resetExamObservationsStore } from './reset-exam-observations';
import { resetRosObservationsStore } from './reset-ros-observations';

export type AppointmentTelemedState = {
  appointment: Appointment | undefined;
  patient: Patient | undefined;
  location: Location | undefined;
  locationVirtual: Location | undefined;
  locations: Location[];
  practitioners?: Practitioner[];
  group?: HealthcareService;
  followUpOriginEncounter: Encounter;
  encounter: Encounter;
  followupEncounters?: Encounter[];
  selectedEncounterId: string | undefined;
  questionnaireResponse: QuestionnaireResponse | undefined;
  patientConditionPhotos: PatientConditionPhotoRef[];
  schoolWorkNoteUrls: string[];
};

type AppointmentStateUpdater = {
  appointmentError: any;
  appointmentRefetch: () => any;
  appointmentSetState: (
    updater:
      | Partial<AppointmentTelemedState & AppointmentRawResourcesState & InPersonAppointmentState>
      | ((
          state: AppointmentTelemedState & AppointmentRawResourcesState & InPersonAppointmentState
        ) => Partial<AppointmentTelemedState & AppointmentRawResourcesState & InPersonAppointmentState>)
  ) => void;
  setSelectedEncounter: (encounterId: string | undefined) => void;
  getSelectedEncounter: () => Encounter | undefined;
};

type AppointmentRawResourcesState = {
  rawResources: AppointmentResources[];
};

type InPersonAppointmentState = {
  resources: VisitResources;
  mappedData: VisitMappedData;
  visitState: Partial<{
    appointment: Appointment;
    patient: Patient;
    location: Location;
    encounter: Encounter;
    questionnaireResponse: QuestionnaireResponse;
  }>;

  // todo: remove duplication
  isAppointmentLoading: boolean;
};

type ReactQueryState = {
  error: any;
  isLoading: boolean;
  refetch: (options?: RefetchOptions | undefined) => Promise<unknown>;
  isFetching: boolean;
  isPending: boolean;
};

export type ChartDataResponse = Omit<
  GetChartDataResponse,
  Exclude<
    RequestedFields,
    'medications' | 'inhouseMedications' | 'observations' | 'procedures' | 'patientHasPreviousVisits' | 'aiChat'
  >
>;

export type ChartDataState = {
  chartData: ChartDataResponse | undefined;

  // todo: remove duplication
  isChartDataLoading: boolean;
};

const APPOINTMENT_INITIAL: AppointmentTelemedState & AppointmentRawResourcesState & InPersonAppointmentState = {
  appointment: undefined,
  patient: undefined,
  location: undefined,
  locationVirtual: undefined,
  locations: [],
  practitioners: [],
  followUpOriginEncounter: {} as Encounter,
  encounter: {} as Encounter,
  followupEncounters: [],
  selectedEncounterId: undefined,
  questionnaireResponse: undefined,
  patientConditionPhotos: [],
  schoolWorkNoteUrls: [],
  rawResources: [],

  // todo: remove and use selectors/utils or something else, keep in store derived data is not a good decision
  resources: {},
  mappedData: {},
  visitState: {},

  isAppointmentLoading: true, // todo: remove duplication
};

export const APP_TELEMED_LOCAL_INITIAL = {
  currentTab: 'hpi',
};

export const useAppTelemedLocalStore = create<typeof APP_TELEMED_LOCAL_INITIAL>()(() => ({
  ...APP_TELEMED_LOCAL_INITIAL,
}));

export const useAppointmentData = (
  appointmentIdFromProps?: string
): AppointmentTelemedState &
  AppointmentRawResourcesState &
  InPersonAppointmentState &
  AppointmentStateUpdater &
  ReactQueryState => {
  const { id: appointmentIdFromUrl } = useParams();
  const [searchParams] = useSearchParams();
  const appointmentId = appointmentIdFromProps || appointmentIdFromUrl;
  const queryClient = useQueryClient();
  const { data: currentState, isLoading, isFetching, refetch, error, isPending } = useGetAppointment({ appointmentId });

  const setState = useCallback(
    (
      updater:
        | Partial<AppointmentTelemedState & AppointmentRawResourcesState & InPersonAppointmentState>
        | ((
            state: AppointmentTelemedState & AppointmentRawResourcesState & InPersonAppointmentState
          ) => Partial<AppointmentTelemedState & AppointmentRawResourcesState & InPersonAppointmentState>)
    ) => {
      queryClient.setQueryData(
        [TELEMED_APPOINTMENT_QUERY_KEY, appointmentId],
        (prevData: (AppointmentTelemedState & AppointmentRawResourcesState & InPersonAppointmentState) | undefined) => {
          const currentData = prevData || currentState || APPOINTMENT_INITIAL;

          if (typeof updater === 'function') {
            const updates = updater(currentData);
            return { ...currentData, ...updates };
          }

          return { ...currentData, ...updater };
        }
      );

      void queryClient.invalidateQueries({
        queryKey: [TELEMED_APPOINTMENT_QUERY_KEY, appointmentId],
        exact: false,
        refetchType: 'none',
      });
    },
    [queryClient, appointmentId, currentState]
  );

  const setSelectedEncounter = useCallback(
    (encounterId: string | undefined) => {
      setState((state) => ({
        ...state,
        selectedEncounterId: encounterId,
      }));
    },
    [setState]
  );

  useEffect(() => {
    const encounterIdFromUrl = searchParams.get('encounterId');
    if (!encounterIdFromUrl) {
      return;
    }

    const state = currentState || APPOINTMENT_INITIAL;
    if (state.selectedEncounterId === encounterIdFromUrl) {
      return;
    }

    if (isLoading || isPending) {
      return;
    }

    const allEncounters = [state.followUpOriginEncounter, ...(state.followupEncounters || [])].filter(Boolean);
    const encounterExists = allEncounters.some((enc) => enc.id === encounterIdFromUrl);

    if (!encounterExists) {
      queryClient.setQueryData(
        [TELEMED_APPOINTMENT_QUERY_KEY, appointmentId],
        (prevData: (AppointmentTelemedState & AppointmentRawResourcesState & InPersonAppointmentState) | undefined) => {
          const currentData = prevData || state;
          return {
            ...currentData,
            selectedEncounterId: encounterIdFromUrl,
          };
        }
      );

      void queryClient.invalidateQueries({
        queryKey: [TELEMED_APPOINTMENT_QUERY_KEY, appointmentId],
        exact: false,
      });
      return;
    }

    setSelectedEncounter(encounterIdFromUrl);
  }, [isLoading, isPending, searchParams, setSelectedEncounter, currentState, queryClient, appointmentId]);

  const getSelectedEncounter = useCallback(() => {
    const state = currentState || APPOINTMENT_INITIAL;
    if (!state.selectedEncounterId || state.selectedEncounterId === state.followUpOriginEncounter?.id) {
      return state.followUpOriginEncounter;
    }

    return state.followupEncounters?.find((encounter) => encounter.id === state.selectedEncounterId);
  }, [currentState]);

  const fullState = useMemo(() => {
    const state = currentState || APPOINTMENT_INITIAL;
    const selectedEncounter = getSelectedEncounter();
    const encounterToUse = selectedEncounter || state.followUpOriginEncounter;

    let appointmentToUse = state.appointment;
    if (encounterToUse?.appointment?.[0]?.reference) {
      const encApptId = encounterToUse.appointment[0].reference.replace('Appointment/', '');
      if (encApptId !== appointmentToUse?.id) {
        const matchingAppointment = state.rawResources?.find(
          (r): r is Appointment => r.resourceType === 'Appointment' && r.id === encApptId
        );
        if (matchingAppointment) {
          appointmentToUse = matchingAppointment;
        }
      }
    }

    let questionnaireResponseToUse = state.questionnaireResponse;
    if (encounterToUse?.id) {
      const matchingQR = state.rawResources?.find(
        (r): r is QuestionnaireResponse =>
          r.resourceType === 'QuestionnaireResponse' && r.encounter?.reference === `Encounter/${encounterToUse.id}`
      );
      if (matchingQR) {
        questionnaireResponseToUse = matchingQR;
      }
    }

    return {
      ...state,
      appointment: appointmentToUse,
      encounter: encounterToUse,
      questionnaireResponse: questionnaireResponseToUse,
      visitState: {
        ...state.visitState,
        appointment: appointmentToUse,
        encounter: encounterToUse,
        questionnaireResponse: questionnaireResponseToUse,
      },
      resources: {
        ...state.resources,
        appointment: getAppointmentValues(appointmentToUse),
        encounter: getEncounterValues(encounterToUse),
      },
      isAppointmentLoading: isLoading,
      appointmentRefetch: refetch,
      appointmentSetState: setState,
      appointmentError: error,
      setSelectedEncounter,
      getSelectedEncounter,
      isFetching,
      isPending,
      error,
      isLoading,
      refetch,
    };
  }, [
    currentState,
    isLoading,
    setState,
    refetch,
    error,
    isFetching,
    isPending,
    setSelectedEncounter,
    getSelectedEncounter,
  ]);

  return fullState;
};

export type AppointmentResources =
  | Appointment
  | DocumentReference
  | Encounter
  | Location
  | HealthcareService
  | Practitioner
  | Patient
  | QuestionnaireResponse;

const selectAppointmentData = (
  data: AppointmentResources[] | undefined,
  preserveSelectedEncounterId?: string,
  appointmentId?: string
): (AppointmentTelemedState & InPersonAppointmentState & AppointmentRawResourcesState) | null => {
  if (!data) return null;

  const parsed = parseBundle(data);
  const appointments = data.filter(
    (resource: FhirResource) => resource.resourceType === 'Appointment'
  ) as Appointment[];
  const allEncountersForSelection = data.filter(
    (resource: FhirResource) => resource.resourceType === 'Encounter'
  ) as Encounter[];
  const appointment = (
    appointmentId
      ? appointments.find((resource) => resource.id === appointmentId)
      : appointments.find((apt) =>
          allEncountersForSelection.some(
            (enc) => !enc.partOf && enc.appointment?.some((ref) => ref.reference === `Appointment/${apt.id}`)
          )
        ) ?? appointments[0]
  ) as Appointment;
  const patient = data?.find((resource: FhirResource) => resource.resourceType === 'Patient') as Patient;

  const appointmentLocationRef = appointment?.participant?.find((p) => p.actor?.reference?.startsWith('Location/'))
    ?.actor?.reference;
  const appointmentLocationId = appointmentLocationRef?.split('/')?.pop();
  const location = data.find(
    (resource): resource is Location =>
      resource.resourceType === 'Location' &&
      resource.id === appointmentLocationId &&
      // Already scoped to the appointment's own Location; accept it as the physical
      // location when it's in-person (including dual-mode Locations that are also virtual).
      isLocationInPerson(resource)
  );

  const allEncountersRaw = data?.filter(
    (resource: FhirResource) => resource.resourceType === 'Encounter'
  ) as Encounter[];
  const appointmentRef = `Appointment/${appointment?.id}`;
  let followUpOriginEncounter = allEncountersRaw?.find(
    (e) => !e.partOf && e.appointment?.some((encAppointmentRef) => encAppointmentRef.reference === appointmentRef)
  ) as Encounter;
  const followupEncounters = allEncountersRaw?.filter((e) => e.partOf) as Encounter[] | undefined;

  // For scheduled follow-up appointments, the encounter has partOf set.
  // If no non-partOf encounter exists, use the encounter that references this appointment.
  if (!followUpOriginEncounter && allEncountersRaw?.length > 0) {
    followUpOriginEncounter = allEncountersRaw.find(
      (e) => e.appointment?.some((encAppointmentRef) => encAppointmentRef.reference === appointmentRef)
    ) as Encounter;
  }

  // Preserve the selected encounter ID if it exists and is valid, otherwise default to main encounter
  const allEncounters = [followUpOriginEncounter, ...(followupEncounters || [])].filter(Boolean);
  const validSelectedEncounterId =
    preserveSelectedEncounterId && allEncounters.some((enc) => enc.id === preserveSelectedEncounterId)
      ? preserveSelectedEncounterId
      : followUpOriginEncounter?.id;
  const selectedEncounter = allEncounters.find((enc) => enc.id === validSelectedEncounterId) ?? followUpOriginEncounter;
  const questionnaireResponse = data
    ?.filter((resource: FhirResource) => resource.resourceType === 'QuestionnaireResponse')
    .find(
      (resource) =>
        (resource as QuestionnaireResponse).encounter?.reference === `Encounter/${selectedEncounter?.id}` ||
        (resource as QuestionnaireResponse).encounter?.reference === `Encounter/${followUpOriginEncounter?.id}`
    ) as QuestionnaireResponse;

  return {
    rawResources: data,
    appointment,
    patient,
    location,
    locationVirtual: (data?.filter((resource) => resource.resourceType === 'Location') || []).find(isLocationVirtual),
    locations: data?.filter((resource): resource is Location => resource.resourceType === 'Location'),
    practitioners: data?.filter((resource): resource is Practitioner => resource.resourceType === 'Practitioner'),
    group: data?.find((resource): resource is HealthcareService => resource.resourceType === 'HealthcareService'),
    followUpOriginEncounter,
    encounter: followUpOriginEncounter, // Default to main encounter, will be updated by hook
    followupEncounters,
    selectedEncounterId: validSelectedEncounterId,
    questionnaireResponse,
    patientConditionPhotos: extractPatientConditionPhotoRefsFromAppointmentData(data),
    schoolWorkNoteUrls:
      (data
        ?.filter(
          (resource: FhirResource) =>
            resource.resourceType === 'DocumentReference' &&
            resource.status === 'current' &&
            (resource.type?.coding?.[0].code === SCHOOL_WORK_NOTE_CODE ||
              resource.type?.coding?.[0].code === SCHOOL_WORK_NOTE_TEMPLATE_CODE)
        )
        .flatMap((docRef: FhirResource) => (docRef as DocumentReference).content.map((cnt) => cnt.attachment.url))
        .filter(Boolean) as string[]) || [],
    resources: parsed.resources,
    mappedData: parsed.mappedData,

    // todo: remove
    visitState: {
      appointment,
      patient,
      location,
      encounter: followUpOriginEncounter,
      questionnaireResponse,
    },

    isAppointmentLoading: false,
  };
};

const useGetAppointment = (
  {
    appointmentId,
  }: {
    appointmentId: string | undefined;
  },
  onSuccess?: (data: (AppointmentTelemedState & InPersonAppointmentState & AppointmentRawResourcesState) | null) => void
): UseQueryResult<
  (AppointmentTelemedState & InPersonAppointmentState & AppointmentRawResourcesState) | null,
  unknown
> => {
  const { oystehr } = useApiClients();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: [TELEMED_APPOINTMENT_QUERY_KEY, appointmentId],
    queryFn: async () => {
      if (oystehr && appointmentId) {
        // Get the current selected encounter ID from the query cache to preserve it
        const currentData = queryClient.getQueryData([TELEMED_APPOINTMENT_QUERY_KEY, appointmentId]) as
          | (AppointmentTelemedState & InPersonAppointmentState & AppointmentRawResourcesState)
          | undefined;
        const currentSelectedEncounterId = currentData?.selectedEncounterId;

        const data = (
          await oystehr.fhir.search<AppointmentResources>({
            resourceType: 'Appointment',
            params: [
              { name: '_id', value: appointmentId },
              {
                name: '_include',
                value: 'Appointment:patient',
              },
              {
                name: '_include',
                value: 'Appointment:location',
              },
              {
                name: '_include',
                value: 'Appointment:practitioner',
              },
              {
                name: '_include',
                value: 'Appointment:actor:HealthcareService',
              },
              {
                name: '_include:iterate',
                value: 'Encounter:participant:Practitioner',
              },
              {
                name: '_revinclude:iterate',
                value: 'Encounter:appointment',
              },
              {
                name: '_include:iterate',
                value: 'Encounter:location',
              },
              {
                name: '_revinclude:iterate',
                value: 'Encounter:part-of',
              },
              {
                name: '_include:iterate',
                value: 'Encounter:appointment',
              },
              {
                name: '_revinclude:iterate',
                value: 'QuestionnaireResponse:encounter',
              },
              { name: '_revinclude', value: 'DocumentReference:related' },
            ],
          })
        )
          .unbundle()
          .filter(
            (resource) =>
              resource.resourceType !== 'QuestionnaireResponse' ||
              resource.questionnaire?.includes('https://ottehr.com/FHIR/Questionnaire/intake-paperwork-inperson') ||
              resource.questionnaire?.includes('https://ottehr.com/FHIR/Questionnaire/intake-paperwork-virtual')
          );

        // For scheduled follow-ups: the encounter has partOf referencing a parent encounter.
        // We need to fetch the parent encounter's full tree to show all encounters in the sidebar.
        const scheduledFollowupEncounter = data.find(
          (r) => r.resourceType === 'Encounter' && (r as Encounter).partOf
        ) as Encounter | undefined;

        if (scheduledFollowupEncounter?.partOf?.reference) {
          const parentEncounterId = scheduledFollowupEncounter.partOf.reference.replace('Encounter/', '');
          // Fetch the parent encounter to find its appointment, then fetch the full tree
          const parentEncounter = await oystehr.fhir.get<Encounter>({
            resourceType: 'Encounter',
            id: parentEncounterId,
          });
          const parentAppointmentId = parentEncounter.appointment?.[0]?.reference?.replace('Appointment/', '');

          if (parentAppointmentId && parentAppointmentId !== appointmentId) {
            const parentData = (
              await oystehr.fhir.search<AppointmentResources>({
                resourceType: 'Appointment',
                params: [
                  { name: '_id', value: parentAppointmentId },
                  { name: '_revinclude:iterate', value: 'Encounter:appointment' },
                  { name: '_revinclude:iterate', value: 'Encounter:part-of' },
                  { name: '_include:iterate', value: 'Encounter:appointment' },
                  { name: '_revinclude:iterate', value: 'QuestionnaireResponse:encounter' },
                ],
              })
            ).unbundle();

            // Merge parent tree data, avoiding duplicates
            const existingIds = new Set(data.map((r) => `${r.resourceType}/${r.id}`));
            for (const resource of parentData) {
              const key = `${resource.resourceType}/${resource.id}`;
              if (!existingIds.has(key)) {
                data.push(resource);
                existingIds.add(key);
              }
            }
          }
        }

        // Only fall back to the scheduled follow-up encounter when the URL actually targets it.
        // Otherwise, on a main-appointment URL the bundle's child follow-up encounter would hijack
        // the selection and vitals would get saved against the follow-up's id.
        const hasMainEncounterForRequestedAppt = data.some(
          (r) =>
            r.resourceType === 'Encounter' &&
            !(r as Encounter).partOf &&
            (r as Encounter).appointment?.some((ref) => ref.reference === `Appointment/${appointmentId}`)
        );
        const fallbackEncounterId = hasMainEncounterForRequestedAppt ? undefined : scheduledFollowupEncounter?.id;

        return selectAppointmentData(data, currentSelectedEncounterId || fallbackEncounterId, appointmentId);
      }
      throw new Error('fhir client not defined or appointmentId not provided');
    },
    enabled: Boolean(oystehr) && Boolean(appointmentId),
    staleTime: 5_000, // fast fix for the https://github.com/masslight/ottehr/issues/3776; It might be related to rerenders triggering refetching and React Query getting stuck in an infinite loading loop
  });

  const data = query.data;
  useSuccessQuery(data, (data) => data && onSuccess && onSuccess(data));

  return query;
};

export const useSaveChartData = (): UseMutationResult<
  PromiseReturnType<ReturnType<OystehrTelemedAPIClient['saveChartData']>>,
  Error,
  Omit<SaveChartDataRequest, 'encounterId'>
> => {
  const apiClient = useOystehrAPIClient();
  const { encounter } = useAppointmentData();
  const { isAppointmentReadOnly: isReadOnly } = useGetAppointmentAccessibility();

  return useMutation({
    mutationFn: (chartDataFields: Omit<SaveChartDataRequest, 'encounterId'>) => {
      // disabled saving chart data in read only mode except addendum note (legacy single-string field
      // and the per-author `notes` array entries of type ADDENDUM, which providers can still append
      // after the visit is signed and the claim is created)
      if (isReadOnly) {
        const onlyAddendumChanges = Object.keys(chartDataFields).every((key) => {
          const field = key as keyof AllChartValues;
          if (field === 'addendumNote') return true;
          if (field === 'notes') {
            return (chartDataFields.notes ?? []).every((n) => n.type === NOTE_TYPE.ADDENDUM);
          }
          return false;
        });
        if (!onlyAddendumChanges) {
          throw new Error('update disabled in read only mode');
        }
      }

      if (apiClient && encounter?.id) {
        return apiClient.saveChartData({
          encounterId: encounter.id,
          ...chartDataFields,
        });
      }
      throw new Error('api client not defined or encounterId not provided');
    },
    retry: 2,
  });
};

export const useDeleteChartData = (): UseMutationResult<
  PromiseReturnType<ReturnType<OystehrTelemedAPIClient['deleteChartData']>>,
  Error,
  Omit<DeleteChartDataRequest, 'encounterId'>
> => {
  const apiClient = useOystehrAPIClient();
  const { encounter } = useAppointmentData();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (chartDataFields: Omit<DeleteChartDataRequest, 'encounterId'>) => {
      if (apiClient && encounter?.id) {
        return apiClient.deleteChartData({
          encounterId: encounter.id,
          ...chartDataFields,
        });
      }
      throw new Error('api client not defined or encounterId not provided');
    },
    onError: async (error) => {
      if ((error as any).code === APIErrorCode.FHIR_RESOURCE_IS_GONE) {
        // Usually this happens due to an attempt to delete an already deleted resource. Thus full state refresh is required.
        resetExamObservationsStore();
        resetRosObservationsStore();
        await invalidateChart(queryClient, encounter.id);
      }
    },
    retry: 2,
  });
};

export const TELEMED_APPOINTMENT_QUERY_KEY = 'telemed-appointment';
